import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { first } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { Workbook } from 'exceljs';
import * as fs from 'file-saver';
import { Store } from '@ngrx/store';
import { selectCountsCurrentIdState } from '@app/modules/counts/store/selectors/counts.selectors';
import * as moment from 'moment';
import { Observable, Subscription } from 'rxjs';
import * as IntegrationsActions from '@app/modules/integrations/store/actions/integrations.actions'
import { ToastrService } from 'ngx-toastr';
import * as CoreActions from '@app/core/store/core.action';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class QuickBooksService {
  public endpoint$: string = `${environment.apiUrl}/quick-books`;

  constructor(
    private _http: HttpClient,
    private _store: Store,
    private _translation: TranslateService,
    private _toastr: ToastrService,
  ) { }

  public generateCountExcel() {
    return this._store.select(selectCountsCurrentIdState).pipe(first()).subscribe((id) => {
      this._http.get(`${this.endpoint$}/counts/${id}`).pipe(first()).subscribe((response) => {
        let invItems = response['data'].products;
        const header = [
          'Product/Service',
          'Type',
          'Description',
          'Price',
          'Cost',
          'Quantity On Hand',
          'SKU',
          'Quantity as-of Date',
          'Reorder Point'
        ];

        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Sharing Data');
        const headerRow = worksheet.addRow(header);

        headerRow.eachCell((cell, number) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'E1F4E4' },
            bgColor: { argb: 'FFFFFF' }
          };
          cell.font = {
            color: {
              argb: "000000"
            }
          }
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        invItems.forEach(item => {
          worksheet.addRow([
            item.prdNameEn,
            "Inventory",
            item.prdDescription,
            item.pcmRetail,
            item.pcmCost,
            item.invitmCount,
            item.prdCode,
            moment(item.invitmTS).format('DD/MM/YYYY'),
            item.reorder
          ]);
        });

        worksheet.getColumn(1).width = 40;
        worksheet.getColumn(2).width = 10;
        worksheet.getColumn(3).width = 60;
        worksheet.getColumn(4).width = 20;
        worksheet.getColumn(5).width = 20;
        worksheet.getColumn(6).width = 25;
        worksheet.getColumn(7).width = 25;
        worksheet.getColumn(7).width = 25;

        workbook.xlsx.writeBuffer().then((data: any) => {
          const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          fs.saveAs(blob, 'QuickBooksCountExport.xlsx');
        });
      });
    });
  }

  public generateProductsExcel() {
      return this._http.get(`${this.endpoint$}/products-excel`).pipe(first()).subscribe((response) => {
        let invItems = response['data'].products
        const header = [
          'Product/Service',
          'Type',
          'Description',
          'Price',
          'Cost',
          'Quantity On Hand',
          'SKU',
          'Quantity as-of Date',
        ];

        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Sharing Data');
        const headerRow = worksheet.addRow(header);

        headerRow.eachCell((cell, number) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'E1F4E4' },
            bgColor: { argb: 'FFFFFF' }
          };
          cell.font = {
            color: {
              argb: "000000"
            }
          }
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        invItems.forEach(item => {
          worksheet.addRow([
            item.prdNameEn,
            "Inventory",
            item.prdDescription,
            item.pcmRetail,
            item.pcmCost,
            item.invitmCount === null ? 0 : item.invitmCount ,
            item.prdCode,
            item.invitmTS == null ? moment().format('DD/MM/YYYY') : moment(item.invitmTS).format('DD/MM/YYYY')
          ]);
        });

        worksheet.getColumn(1).width = 40;
        worksheet.getColumn(2).width = 10;
        worksheet.getColumn(3).width = 60;
        worksheet.getColumn(4).width = 20;
        worksheet.getColumn(5).width = 20;
        worksheet.getColumn(6).width = 25;
        worksheet.getColumn(7).width = 25;
        worksheet.getColumn(8).width = 25;

        workbook.xlsx.writeBuffer().then((data: any) => {
          const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          fs.saveAs(blob, 'QuickBooksCountExport.xlsx');
        });
      });
  }

  public connect(): Subscription{

    return this._http.get(`${this.endpoint$}/authenticate`).pipe().subscribe(async (response)=> {
      let parameters = "location=1,width=800,height=650";
      parameters += ",left=" + (screen.width - 800) / 2 + ",top=" + (screen.height - 650) / 2;
      var newWin = window.open(`${response['data'].authUrl}`, 'connectPopup', parameters);

     if(!newWin || newWin.closed || typeof newWin.closed=='undefined') 
      { 
        this._store.dispatch(CoreActions.showErrorMessage({
          title: this._translation.instant('errormessage.popupblocker_title'),
          body: this._translation.instant('errormessage.popupblocker_desc'),
          show_upgrade: false
        }));
      }

      let isClosed:any = await new Promise(resolve => window.addEventListener('quickbooksPopup', resolve));

      if(isClosed.detail.accessToken === undefined){
        return alert('There was an error getting your access token');
      }

      if(isClosed.detail.accessToken != undefined){

        await this._http.post(`${this.endpoint$}/save_settings`, {
          accessToken: isClosed.detail.accessToken,
          refreshToken: isClosed.detail.refreshToken,
          realmId: isClosed.detail.realmId
          }).pipe(first()).subscribe((response) => {
            this._store.dispatch(IntegrationsActions.toggleQuickBooks({ active: true }));
            return;
          });
        
      }
      else{
        this.disconnect();
      }

    });
  }

  public getAccessToken(code,realmId): Observable<any>{
    return this._http.post(`${this.endpoint$}/access-token`, {
      code,
      realmId
    });
  }

  public setAccessToken(token){
    if(token!=null && token!=undefined && token!=''){
      //return localStorage.setItem('trakrCountingQbAccessToken', this.encryptionService.encryptData(token));
    }
  }

  public setRefreshToken(token){
    if(token!=null && token!=undefined && token!=''){
     // return localStorage.setItem('trakrCountingQbRefreshToken', this.encryptionService.encryptData(token));
    }
  }

  public getRefreshToken(token):Observable<any>{
    return this._http.post(`${environment.apiUrl}/quick-books/refresh-token`, {});
  }

  public refreshToken(){
    return this._http.get(`${environment.apiUrl}/quick-books/refresh-token`, {
    });
    //const key = localStorage.getItem('trakrCountingQbRefreshToken');
    //if(key!=null && key!=undefined && key!=''){
      //return localStorage.getItem('trakrCountingQbRefreshToken');
    //}
  }

  public syncProductsToConfirm(): Observable<any>{
    return this._http.post(`${environment.apiUrl}/quick-books/products/sync-to-confirm`, {
    }, {
    });
  }

  public exportProductsService(): Observable<any>{
    return this._http.get(`${environment.apiUrl}/quick-books/products/sync-to`, {});
  }

  /*public syncProductsFromConfirm(): Observable<any>{
    return this._http.get(`${environment.apiUrl}/quick-books/products/sync-from-confirm`, {
    });
  }*/

  public importProductsService(fields): Observable<any>{
    return this._http.get(`${environment.apiUrl}/quick-books/products/sync-from`, {
      params: fields
    });
  }

  /*public syncInventoryConfirm(id): Observable<any>{
    return this._http.post(`${environment.apiUrl}/quick-books/inventory-confirm/sync-to/${id}`, {
    }, {
     headers: {
       trakrCountingQbAccessToken: !!localStorage.getItem('trakrCountingQbAccessToken') ? localStorage.getItem('trakrCountingQbAccessToken') : null ,
        trakrCountingQbRefreshToken: !!localStorage.getItem('trakrCountingQbRefreshToken') ? localStorage.getItem('trakrCountingQbRefreshToken') : null,
        trakrCountingRealmId: !!localStorage.getItem('trakrCountingRealmId') ? localStorage.getItem('trakrCountingRealmId') : null,
     }
    });
  }*/

  public exportCountService(id,type:string): Observable<any>{
    return this._http.get(`${environment.apiUrl}/quick-books/inventory/sync-to/${id}?count_type=${type}`);
  }

  public importCountService(): Observable<any>{
    return this._http.get(`${environment.apiUrl}/quick-books/inventory/sync-from-inventory-count`);
  }

  public init(): Observable<any>{
    return this._http.post(`${environment.apiUrl}/quick-books/validate-access-token`, {});
  }

  public disconnect(): Observable<any>{
    return this._http.get(`${environment.apiUrl}/quick-books/disconnect`)

  }

  public getMostRecent(){
    return this._http.get(`${environment.apiUrl}/quick-books/products/most-recent`);
  }

  public getSyncs(){
    return this._http.get(`${environment.apiUrl}/quick-books/products/syncs`);
  }

  public getSyncProducts(id): Observable<any>{
    return this._http.get(`${environment.apiUrl}/quick-books/products/syncs/${id}`);
  }

}
